/**
 * Orientation diagnostics for the force-vertical rendering pipeline.
 *
 * Two different one-shot signals are emitted from this module:
 *
 * 1. `reportOrientationMismatch` — the original 180° flip diagnostic. Fires when
 *    matchMedia, screen.orientation, and innerHeight-vs-Width disagree at the
 *    moment a force-rotation is being applied. Detects the iOS Safari race that
 *    motivated moving rotation from React inline styles into CSS @media rules.
 *
 * 2. `reportForceLandscapeNotApplied` — the regression diagnostic. Fires when an
 *    element marked with `data-force-landscape='true'` ends up with a computed
 *    `transform` of `none` while `(orientation: portrait)` matches. Detects the
 *    Android Chrome class of bugs that broke force-vertical rendering across
 *    Samsung devices (containing-block hijack by transformed ancestors, Tailwind
 *    utility specificity ties beating the rule, `100dvh`/`100dvw` resolution
 *    bugs inside fixed/transform contexts).
 *
 * Each diagnostic is independently one-shot per session and dispatches a custom
 * event for any analytics layer to subscribe to.
 */
let hasReportedMismatch = false;
let hasReportedNotApplied = false;

export interface OrientationDiagnosticSnapshot {
  callsite: string;
  matchMediaPortrait: boolean;
  innerPortrait: boolean;
  screenPortrait: boolean;
  orientationType: string;
  angle: number;
  innerWidth: number;
  innerHeight: number;
  visualWidth: number | undefined;
  visualHeight: number | undefined;
  timestamp: string;
}

export function reportOrientationMismatch(callsite: string): void {
  if (hasReportedMismatch) return;
  if (typeof window === 'undefined') return;

  const matchMediaPortrait = window.matchMedia('(orientation: portrait)').matches;
  const innerPortrait = window.innerHeight > window.innerWidth;
  const screenOrientation = window.screen?.orientation;
  const angle = screenOrientation?.angle ?? 0;
  const orientationType = screenOrientation?.type ?? 'unknown';
  const screenPortrait = orientationType.startsWith('portrait');

  const inViewportAgreement = matchMediaPortrait === innerPortrait;
  const inScreenAgreement = screenOrientation
    ? matchMediaPortrait === screenPortrait
    : true;

  if (inViewportAgreement && inScreenAgreement) return;

  hasReportedMismatch = true;
  const snapshot: OrientationDiagnosticSnapshot = {
    callsite,
    matchMediaPortrait,
    innerPortrait,
    screenPortrait,
    orientationType,
    angle,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    visualWidth: window.visualViewport?.width,
    visualHeight: window.visualViewport?.height,
    timestamp: new Date().toISOString(),
  };

  console.warn('[orientation-diagnostic] signals disagree', snapshot);
  window.dispatchEvent(
    new CustomEvent('playcast:orientation-mismatch', { detail: snapshot }),
  );
}

export interface ForceLandscapeNotAppliedSnapshot {
  callsite: string;
  computedTransform: string;
  computedPosition: string;
  computedWidth: string;
  computedHeight: string;
  matchMediaPortrait: boolean;
  orientationType: string;
  angle: number;
  innerWidth: number;
  innerHeight: number;
  ancestorTransformChain: Array<{ tag: string; transform: string; filter: string; willChange: string }>;
  timestamp: string;
}

/**
 * Verify that an element marked with `data-force-landscape='true'` actually
 * has a rotation transform applied while the viewport is in portrait. If not,
 * the CSS rule failed (Issue A: containing-block hijack / specificity tie /
 * Android dvh quirks) — fire a one-shot diagnostic with the computed style
 * and the chain of ancestors with non-`none` transform/filter/will-change so
 * the regression can be triaged from production telemetry.
 *
 * The defensive inline-style fallback in Player.tsx normally rescues the
 * visual outcome even when this fires, but the signal still indicates a
 * cascade problem worth investigating.
 */
export function reportForceLandscapeNotApplied(
  element: Element | null,
  callsite: string,
): void {
  if (hasReportedNotApplied) return;
  if (typeof window === 'undefined' || !element) return;

  const matchMediaPortrait = window.matchMedia('(orientation: portrait)').matches;
  if (!matchMediaPortrait) return;

  const computed = window.getComputedStyle(element);
  const computedTransform = computed.transform;
  if (computedTransform && computedTransform !== 'none') return;

  hasReportedNotApplied = true;

  const ancestorTransformChain: ForceLandscapeNotAppliedSnapshot['ancestorTransformChain'] = [];
  let current = element.parentElement;
  while (current) {
    const ancestorStyle = window.getComputedStyle(current);
    const transform = ancestorStyle.transform;
    const filter = ancestorStyle.filter;
    const willChange = ancestorStyle.willChange;
    const containsAny =
      (transform && transform !== 'none') ||
      (filter && filter !== 'none') ||
      (willChange && willChange !== 'auto');
    if (containsAny) {
      ancestorTransformChain.push({
        tag: current.tagName.toLowerCase(),
        transform,
        filter,
        willChange,
      });
    }
    current = current.parentElement;
  }

  const screenOrientation = window.screen?.orientation;
  const snapshot: ForceLandscapeNotAppliedSnapshot = {
    callsite,
    computedTransform: computedTransform || 'none',
    computedPosition: computed.position,
    computedWidth: computed.width,
    computedHeight: computed.height,
    matchMediaPortrait,
    orientationType: screenOrientation?.type ?? 'unknown',
    angle: screenOrientation?.angle ?? 0,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    ancestorTransformChain,
    timestamp: new Date().toISOString(),
  };

  console.warn('[orientation-diagnostic] force-landscape not applied', snapshot);
  window.dispatchEvent(
    new CustomEvent('playcast:force-landscape-not-applied', { detail: snapshot }),
  );
}

/** Test-only reset of the one-shot guards. */
export function _resetOrientationDiagnosticForTesting(): void {
  hasReportedMismatch = false;
  hasReportedNotApplied = false;
}
